import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import { RenderingErrorBoundaryChildProps } from 'components/RenderingErrorBoundary';
import AIButton from 'components/shared/ai/AIButton';
import SummaryContentView from 'components/shared/ai/summary/SummaryContentView';
import Button from 'components/shared/Button';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import { SupportEmailLink } from 'components/shared/SupportEmailLink';
import LoadingState from 'components/summarization/LoadingState';
import Header from 'components/summarization/view/Header';
import { useSummarization } from 'context/summarization';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { AIJobType } from 'models/AIJob';
import { summarizationQueryKeys } from 'services/api/summarizations';
import { AIJobNotificationType } from 'stores/aiJobNotificationStore';
import { useMobileNavigationActions } from 'stores/mobileNavigationStore';
import HamburgerMenuIcon from 'svg/HamburgerMenuIcon';
import SummaryFailedIcon from 'svg/SummaryFailedIcon';

export default function ViewSummarization({ isErrored = false }: RenderingErrorBoundaryChildProps) {
  const { setNavigationOpen } = useMobileNavigationActions();
  const navigate = useNavigate();
  const location = useLocation();

  const { summarization } = useSummarization();

  const { attachments, summaryJob, summary } = summarization ?? {};
  const summaryJobInProgress = summaryJob?.isProcessing;

  const [isLoading, setIsLoading] = useState(summaryJobInProgress);

  const { startTracking, isTracking } = useTrackAIJob();

  useEffect(() => {
    if (
      summaryJobInProgress &&
      summarization &&
      !isTracking({ jobbableId: summarization.id, jobType: AIJobNotificationType.WORKSPACE_SUMMARY }) &&
      location.pathname.includes('/summarizations/')
    ) {
      startTracking({
        jobs: [{ id: summaryJob.id, type: AIJobType.SUMMARY }],
        jobbableId: summarization.id,
        summaryType: summarization.title,
        jobType: AIJobNotificationType.WORKSPACE_SUMMARY,
        returnUrl: location.pathname,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryJobInProgress, location.pathname]);

  useEffect(() => {
    if (summaryJobInProgress) {
      setIsLoading(true);
    } else if (summaryJob?.isErrored) {
      setIsLoading(false);
    }
  }, [summaryJobInProgress, summaryJob?.isErrored]);

  const handleLoadingFinished = useCallback(() => {
    setIsLoading(false);
  }, []);

  if (isErrored || !summaryJob || summaryJob.isErrored || (summaryJob.isCompleted && !summary) || !summarization) {
    const isMultiplePatientError = (error: unknown): boolean =>
      typeof error === 'object' &&
      error !== null &&
      (error as { type?: string }).type === 'MultiplePatientDemographicsError';

    const hasMultiplePatientsError =
      isMultiplePatientError(summaryJob?.error) ||
      (!!attachments?.length && attachments.every((att) => isMultiplePatientError(att.extractDataAIJob?.error)));

    const errorText = hasMultiplePatientsError ? (
      'Multiple patients were identified in the source documentation. Documentation must be specific to a single patient.'
    ) : (
      <>
        Something went wrong. Click below to start a new summary or contact <SupportEmailLink />.
      </>
    );

    return (
      <Container>
        <MobileOnly>
          <MobileNavMenu variant='icon' onClick={() => setNavigationOpen(true)} data-cy='hamburgerIconButton'>
            <HamburgerMenuIcon />
          </MobileNavMenu>
        </MobileOnly>
        <ErrorContainer>
          <SummaryFailedIcon />
          <span>
            <b>Summary failed to process.</b> {errorText}
          </span>
          <div style={{ width: 176 }}>
            <AIButton onClick={() => navigate('/summarizations/new')} fullWidth>
              Start New Summary
            </AIButton>
          </div>
        </ErrorContainer>
      </Container>
    );
  }

  return isLoading ? (
    <LoadingWrapper>
      <Header summarization={summarization} />
      <LoadingContainer>
        <LoadingState summarization={summarization} onLoadingFinished={handleLoadingFinished} />
      </LoadingContainer>
    </LoadingWrapper>
  ) : (
    <Page>
      <Header summarization={summarization} />
      <Container>
        <SummaryContentView
          summaryJob={summaryJob}
          summary={summary!}
          attachments={attachments ?? []}
          queryKey={summarizationQueryKeys.show({ id: summarization.id })}
        />
      </Container>
    </Page>
  );
}

const Container = styled(Page.Content)`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px 40px 32px 40px;
  margin: 0;
  width: 100%;
  height: 100%;
`;

const MobileNavMenu = styled(Button)`
  position: absolute;
  top: 24px;
  left: 24px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const LoadingContainer = styled(Container)`
  margin-top: 0;
  flex: 1;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  align-items: center;
  justify-content: start;
  gap: 32px;
  max-width: 640px;
  height: 100%;
  margin-top: 60px;

  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`;
